import {
  BasePlugin,
  BaseStats,
  mergePluginOption,
  EventHandler,
  EventIds,
  ModuleExposureObserverEvent,
  PlainObject,
} from '@fpkg/stats-core';
import { humpToUnderline } from '@fpkg/stats-utils';
// @ts-ignore
import lifecycle from 'page-lifecycle';
import { JsErrorOpt } from './JsErrorPlugin';
import { addWindowUnloadEvent } from '../utils';
// @ts-ignore

/* 模块曝光监控配置 */
export interface ModuleExposureOpt {
  rootSelector?: string; // 监控的根组件selector
  moduleKey?: string; // 监控关键字
}

/**
 * 模块曝光封装类工具类
 * @constructor
 */
class ModuleExposure {
  callback?: EventHandler<ModuleExposureObserverEvent>;

  option: ModuleExposureOpt = {
    rootSelector: 'body',
    moduleKey: 'stats-module-exposure',
  };

  #intersectionObserver?: IntersectionObserver;

  #mutationObserver?: MutationObserver;

  constructor(opt?: ModuleExposureOpt) {
    Object.assign(this.option, opt);

    const rootElement: HTMLElement = document.querySelector(this.option.rootSelector!) as HTMLElement;
    if (rootElement) {
      this.#intersectionObserver = new IntersectionObserver((changes: IntersectionObserverEntry[]) => {
        // 第一次回调时，元素可见和不可见都会调用
        changes.forEach((change) => {
          const { target, intersectionRatio } = change;
          if (intersectionRatio > 0) {
            this.handlerTargetVisible(target as HTMLElement);
          } else {
            this.handlerTargetHidden(target as HTMLElement);
          }
        });
      });
      this.observerExposure(this.#intersectionObserver, rootElement);

      // 监听目标元素的子孙元素添加删除操作
      this.#mutationObserver = new MutationObserver(() => {
        // 如果子元素出现添加删除，激活监听
        this.observerExposure(this.#intersectionObserver!, rootElement);
      });
      this.#mutationObserver.observe(rootElement, {
        childList: true,
        subtree: true,
      });

      lifecycle.addEventListener('statechange', (event: Event | any) => {
        if (event.newState === 'hidden') {
          // 隐藏
          // 离开当前tab，不会触发IntersectionObserver回调，手动计算不可见的元素
          this.activeModuleHidden(rootElement);
          // 断开监听，等回到tab，再次激活
          this.#intersectionObserver!.disconnect();
        } else if (event.newState === 'passive') {
          // 显示
          // 回到当前tab,不会触发IntersectionObserver回调，再次激活可见的元素
          this.observerExposure(this.#intersectionObserver!, rootElement);
        }
      });

      // 监听页面卸载事件
      addWindowUnloadEvent(() => {
        this.activeModuleHidden(rootElement);
      });
    }
  }

  observe(callback: EventHandler<ModuleExposureObserverEvent>): void {
    this.callback = callback;
  }

  // 销毁监听,在vue destroyed中调用
  destroy(): void {
    setTimeout(() => {
      this.#intersectionObserver && this.#intersectionObserver.disconnect();
      this.#mutationObserver && this.#mutationObserver.disconnect();
    }, 0);
  }

  // 手动计算模块不可见事件
  activeModuleHidden(rootElement: HTMLElement) {
    const eles = this.getModuleExposureElements(rootElement);
    eles &&
      eles.forEach((item: Node) => {
        this.handlerTargetHidden(item as HTMLElement);
      });
  }

  handlerTargetVisible(target: HTMLElement) {
    // 元素可见,去上报
    // eslint-disable-next-line no-param-reassign
    target.__show_time__ = Date.now();

    this.callback &&
      this.callback({
        target,
        visible: true,
        event_code: this.getModuleExposureElementName(target),
        params: this.getModuleExposureDataset(target),
      });
  }

  handlerTargetHidden(target: HTMLElement) {
    if (!target.__show_time__) return;
    // 元素不可见，去上报
    this.callback &&
      this.callback({
        target,
        visible: false,
        duration: Date.now() - target.__show_time__,
        event_code: this.getModuleExposureElementName(target),
        params: this.getModuleExposureDataset(target),
      });
    // eslint-disable-next-line no-param-reassign
    target.__show_time__ = 0;
  }

  /**
   * 监听元素是否可见
   * @param observer
   */
  observerExposure(observer: IntersectionObserver, rootElement: HTMLElement) {
    if (!observer) return;
    const eles = this.getModuleExposureElements(rootElement);
    if (eles) {
      eles.forEach((item: Node) => {
        observer.observe(item as Element);
      });
    }
  }

  /**
   * 获取需要上报曝光的元素
   * @param element
   */
  getModuleExposureElements(element: HTMLElement): NodeList | null {
    return element ? element.querySelectorAll(`[${this.option.moduleKey}]:not([${this.option.moduleKey}=""])`) : null;
  }

  getModuleExposureElementName(element: HTMLElement): string {
    return element ? (element.getAttribute(<string>this.option.moduleKey) as string) : '';
  }

  getModuleExposureDataset(element: HTMLElement): PlainObject {
    if (!element || !element.dataset || Object.keys(element.dataset).length === 0) return {};

    const dataset: PlainObject = {};
    Object.keys(element.dataset).forEach((key: string) => {
      if (key.indexOf('stats') === 0) {
        const keyNew: string = humpToUnderline(key).replace('stats_', '');
        dataset[keyNew] = element.dataset[key] || '';
      }
    });
    return dataset;
  }
}

export default class ModuleExposurePlugin implements BasePlugin {
  name = 'moduleExposure';

  stats?: BaseStats;

  option: ModuleExposureOpt = {
    rootSelector: 'body', // 监控的根组件selector
    moduleKey: 'stats-module-exposure', // 监控关键字
  };

  constructor(option: JsErrorOpt | null | boolean) {
    mergePluginOption(this.option, option);
  }

  setup(stats: BaseStats): void {
    this.stats = stats;

    // 模块曝光事件
    try {
      const moduleExposure = new ModuleExposure(this.option);
      moduleExposure.observe((event) => {
        const { event_code, params, visible, duration } = event;

        const eventInfo = visible
          ? {
              ...params,
              event: EventIds.ModuleExposure,
              event_code,
              visible,
            }
          : {
              ...params,
              event: EventIds.ModuleExposure,
              event_code,
              visible,
              duration: duration! > 30 * 60 * 1000 ? 30 * 60 * 1000 : duration, // 最大时长为30分钟
            };

        if (this.stats) {
          this.stats.event(eventInfo);
        }
      });
    } catch (e) {
      console.log(e);
    }
  }
}
